import {
  DropdownMenuGroup,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import type { RailRowMenuController } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { ObjectActionMenuItem } from "@/object-actions/renderers/ObjectActionMenuItem";
import type {
  ActionLabelContext,
  ObjectKind,
  ResolvedObjectAction,
} from "@/object-actions/types";
import { groupActionsByCatalogGroup } from "@/object-actions/utils";
import type { ReactNode } from "react";

export interface ObjectActionMenuProps {
  actions: ResolvedObjectAction[];
  kind: ObjectKind;
  name?: string;
  /** Rail row controller — when set, renders via RailRowMenu. */
  menuController?: RailRowMenuController;
  label?: string;
  /** Leading content above the registry actions (e.g. a selection count). */
  header?: ReactNode;
  /** Extra menu items appended after registry actions (e.g. download submenu). */
  children?: ReactNode;
}

function ObjectActionMenuItems({
  actions,
  kind,
  name,
  header,
  children,
}: Omit<ObjectActionMenuProps, "menuController" | "label">) {
  const labelCtx: ActionLabelContext = { kind, name };
  const groups = groupActionsByCatalogGroup(
    actions.filter(
      (a) =>
        a.presentation === "menu-item" ||
        a.presentation === "menu-item-with-shortcut",
    ),
  );

  return (
    <>
      {header}
      {groups.map((group, groupIndex) => (
        <div key={groupIndex}>
          {groupIndex > 0 && <DropdownMenuSeparator />}
          <DropdownMenuGroup>
            {group.map((action) => (
              <ObjectActionMenuItem
                key={action.id}
                action={action}
                labelCtx={labelCtx}
              />
            ))}
          </DropdownMenuGroup>
        </div>
      ))}
      {children}
    </>
  );
}

export function ObjectActionMenu({
  actions,
  kind,
  name,
  menuController,
  label,
  header,
  children,
}: ObjectActionMenuProps) {
  const items = (
    <ObjectActionMenuItems
      actions={actions}
      kind={kind}
      name={name}
      header={header}
    >
      {children}
    </ObjectActionMenuItems>
  );

  if (menuController && label) {
    return (
      <RailRowMenu {...railRowMenuProps(menuController)} label={label}>
        {items}
      </RailRowMenu>
    );
  }

  return items;
}
